import { FilterOption } from "@components/Filter";
import acceleratorData from "@content/en/accelerators/accelerators.yaml";
import acceleratorCategories from "@content/en/accelerators/categories.yaml";
import { stringToURI } from "@scripts/utils";
import Accelerator from "./Accelerator";

const { accelerators } = acceleratorData;
const { categories } = acceleratorCategories;
export const ACCELERATOR_CATEGORIES = categories;

export const getAllAccelerators = () => accelerators;

export const getFeaturedAccelerators = () =>
  accelerators.filter((a) => a.featured);

export const getAcceleratorsByFilters = (
  filterCategory: string,
  filterTag: string
) => {
  const matchingProjects: { heading: string; projects: Accelerator[] }[] = [];
  // Only use completed projects
  let acceleratorsWithFeature = accelerators.filter((a) => a.complete);
  if (filterTag) {
    acceleratorsWithFeature = accelerators.filter((a) => {
      const tags = a.tags.map((t: string) => stringToURI(t));
      return tags.includes(filterTag);
    });
  }
  ACCELERATOR_CATEGORIES.forEach((c) => {
    const categoryAsURI = stringToURI(c);
    if (filterCategory && categoryAsURI !== filterCategory) {
      return;
    }

    const projectsWithFeature = acceleratorsWithFeature.filter(
      (a) => a.category === c
    );
    if (projectsWithFeature.length === 0) {
      return;
    }
    matchingProjects.push({
      heading: c,
      projects: projectsWithFeature,
    });
  });

  return matchingProjects;
};

const FEATURE_MAP = {
  "arduino-firmware": "Arduino",
  "circuitpython-firmware": "CircuitPython",
  "micropython-firmware": "MicroPython",
  "python-firmware": "Python",
  "zephyr-firmware": "Zephyr",
  "gps-gnss-tracking": "GPS/GNSS Tracking",
  lora: "LoRa",
  "machine-learning": "Machine Learning",
  "motion-tracking": "Motion Tracking",
  "wi-fi-triangulation": "Wi-Fi Triangulation",
  "easy-to-build-dashboard": "Easy-to-Build Dashboard",
  "sms-notifications": "SMS Notifications",
  "web-app-provided": "Web App Provided",
  "environment-variables": "Environment Variables",
  "fleet-management": "Fleet Management",
  "notehub-api": "Notehub API",
  "jsonata-event-processing": "JSONata Event Processing",
  "templated-notefiles": "Templated Notefiles",
};

export const getAcceleratorFeatures = (category: string) => {
  const firmwareFeatures: FilterOption[] = [];
  const noteCardHubFeatures: FilterOption[] = [];
  const technologyFeatures: FilterOption[] = [];
  const viewingDataFeatures: FilterOption[] = [];
  const matchingAccelerators = category
    ? accelerators.filter((a) => stringToURI(a.category) === category)
    : accelerators;

  const features = [
    {
      key: "Firmware",
      value: firmwareFeatures,
    },
    {
      key: "Notecard & Notehub",
      value: noteCardHubFeatures,
    },
    {
      key: "Technology",
      value: technologyFeatures,
    },
    {
      key: "Viewing Data",
      value: viewingDataFeatures,
    },
  ];

  const processFeatureCategory = (
    featuresToAdd: string[],
    destinationArray: FilterOption[]
  ) => {
    featuresToAdd.forEach((feature) => {
      const matches = matchingAccelerators.filter((a) => {
        const tags = a.tags.map((t: string) => stringToURI(t));
        return tags.includes(feature);
      });

      destinationArray.push({
        key: feature,
        value: FEATURE_MAP[feature as keyof typeof FEATURE_MAP],
        disabled: matches.length === 0,
      });
    });
  };

  processFeatureCategory(
    [
      "arduino-firmware",
      "circuitpython-firmware",
      "micropython-firmware",
      "python-firmware",
      "zephyr-firmware",
    ],
    firmwareFeatures
  );

  processFeatureCategory(
    [
      "environment-variables",
      "fleet-management",
      "notehub-api",
      "jsonata-event-processing",
      "templated-notefiles",
    ],
    noteCardHubFeatures
  );

  processFeatureCategory(
    [
      "gps-gnss-tracking",
      "lora",
      "machine-learning",
      "motion-tracking",
      "wi-fi-triangulation",
    ],
    technologyFeatures
  );

  processFeatureCategory(
    ["easy-to-build-dashboard", "sms-notifications", "web-app-provided"],
    viewingDataFeatures
  );

  return features;
};
